import React from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useGetTaxEstimation } from '~/hooks/apis/connection-google/useTaxEstimation';

import ApiError from '~/types/ApiError';

import { formatShare, formatTimestamp } from './utils';

export default function TaxEstimationPanel() {
  const query = useGetTaxEstimation();
  if (query.isPending) return <CircularProgress aria-label="Loading tax estimates" />;
  // Hide cached data too if a subsequent request fails.
  if (query.isError) {
    const unauthorised = query.error instanceof ApiError && query.error.status === 403;
    return (
      <Stack spacing={2}>
        <Alert severity="error">
          {unauthorised ? 'You are not authorised to view tax estimates.' : 'Unable to load tax estimates.'}
        </Alert>
        <Button onClick={() => query.refetch()}>Retry</Button>
      </Stack>
    );
  }
  const { cache, properties } = query.data;
  return (
    <Stack spacing={3}>
      {cache.status === 'missing' && <Alert severity="warning">No tax estimate snapshot is cached.</Alert>}
      <Box>
        <Typography variant="h6">Cached snapshot</Typography>
        <Typography>Snapshot date: {cache.snapshotDate ?? 'Not available'}</Typography>
        <Typography>Last refreshed: {formatTimestamp(cache.refreshedAt)}</Typography>
        <Typography>Expires: {formatTimestamp(cache.expiresAt)}</Typography>
        <Typography variant="body2">Refresh and expiry times use your browser timezone.</Typography>
      </Box>
      <TableContainer>
        <Table aria-label="Tax estimates by property">
          <TableHead>
            <TableRow>
              <TableCell>Property</TableCell>
              <TableCell>Market</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell align="right">30-day median</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {properties.flatMap((property) =>
              (property.profiles.length ? property.profiles : [null]).map((profile) => (
                <TableRow key={`${property.propertyId}:${profile?.currency ?? 'missing'}`}>
                  <TableCell>{property.propertyName ?? property.propertyId}</TableCell>
                  <TableCell>{property.market}</TableCell>
                  <TableCell>{profile?.currency ?? '—'}</TableCell>
                  <TableCell align="right">
                    {cache.status === 'missing'
                      ? 'Snapshot missing'
                      : profile
                        ? formatShare(profile.share)
                        : 'No estimate'}
                  </TableCell>
                </TableRow>
              )),
            )}
            {!properties.length && (
              <TableRow>
                <TableCell colSpan={4}>No properties.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
